import { useEffect, useRef } from 'react'
import type { CanvasSlot } from '../components/editor-shared/canvasTypes'
import type { BlockContent } from '../api/types'

/**
 * Broadcasts live editor state to any open live-preview window via BroadcastChannel.
 *
 * The preview window sends `{ type: 'ready' }` on mount; this hook responds
 * immediately with the current state so the preview doesn't have to wait for
 * the next change. Subsequent state changes are debounced at 150 ms.
 */
export function usePreviewBroadcast(
  id: string,
  slots: CanvasSlot[],
  contentMap: Record<string, BlockContent>,
  pageTitle: string,
) {
  const channelRef = useRef<BroadcastChannel | null>(null)

  // Mutable ref keeps the latest state for the "ready" handshake handler
  // without re-running the channel setup effect.
  const latestRef = useRef({ slots, contentMap, pageTitle })
  latestRef.current = { slots, contentMap, pageTitle }

  // Open the channel once per page ID.
  useEffect(() => {
    if (!id) return
    const channel = new BroadcastChannel(`wire-ui:preview:${id}`)
    channelRef.current = channel

    channel.onmessage = (ev: MessageEvent) => {
      if (ev.data?.type === 'ready') {
        channel.postMessage({ type: 'state', ...latestRef.current })
      }
    }

    return () => {
      channel.close()
      channelRef.current = null
    }
  }, [id])

  // Debounced broadcast on every state change.
  useEffect(() => {
    const timer = setTimeout(() => {
      channelRef.current?.postMessage({ type: 'state', slots, contentMap, pageTitle })
    }, 150)
    return () => clearTimeout(timer)
  }, [slots, contentMap, pageTitle])
}
